/**
 * FilePreviewModal - Full-screen file preview with support for images and PDFs
 *
 * Features:
 * - Supports both URL-based files and File objects (Union Type)
 * - Type-safe Props with discriminated unions
 * - Proper cleanup for Blob URLs
 * - Accessibility: ARIA attributes, semantic HTML, keyboard support
 */

import { useEffect, useMemo, useCallback, forwardRef } from "react";
import { Download, X, AlertCircle } from "@semo/icons";
import { BodyPortal, Button } from "@semo/shared-ui";
import { cn } from "@semo/shared-ui";

/**
 * Union type to support both URL-based and File objects
 */
type FileData =
  | {
      url: string;
      fileName: string;
      fileSize: number;
      isImage: boolean;
    }
  | {
      file: File;
      isImage?: boolean;
    };

export interface FilePreviewModalProps {
  /** File data - either URL-based or File object */
  file: FileData;
  /** Callback when modal should close */
  onClose: () => void;
  /** Custom download handler (optional) */
  onDownload?: (url: string, fileName: string) => void;
}

/**
 * FilePreviewModal Component
 *
 * Displays full-screen preview of images and PDFs
 * Supports both remote files (URL) and local files (File objects)
 */
export const FilePreviewModal = forwardRef<
  HTMLDivElement,
  FilePreviewModalProps
>(({ file, onClose, onDownload }, ref) => {
  const isUrlFile = "url" in file;

  /**
   * Memoize display values to prevent unnecessary recalculations
   */
  const {
    displayUrl,
    displayFileName,
    displayFileSize,
    isPdf,
    canPreview,
    isImage,
  } = useMemo(() => {
    const fileName = isUrlFile ? file.fileName : file.file.name;
    const fileSize = isUrlFile ? file.fileSize : file.file.size;
    const ext = fileName.split(".").pop()?.toLowerCase() ?? "";
    const isFilePdf = ext === "pdf";
    const url = isUrlFile ? file.url : URL.createObjectURL(file.file);

    // Check if image
    const imageExts = ["png", "jpg", "jpeg", "gif", "webp", "svg", "bmp"];
    const isImageFile = isUrlFile
      ? file.isImage
      : (file.isImage ?? imageExts.includes(ext));

    return {
      displayUrl: url,
      displayFileName: fileName,
      displayFileSize: fileSize,
      isPdf: isFilePdf,
      canPreview: isImageFile || isFilePdf,
      isImage: isImageFile,
    };
  }, [file, isUrlFile]);

  /**
   * Handle file download
   */
  const handleDownload = useCallback(() => {
    if (onDownload) {
      onDownload(displayUrl, displayFileName);
    } else {
      // Default download behavior
      const link = document.createElement("a");
      link.href = displayUrl;
      link.download = displayFileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }, [displayUrl, displayFileName, onDownload]);

  /**
   * Close modal with Escape key support
   */
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  /**
   * Cleanup: Revoke Blob URL if it was created from File object
   */
  useEffect(() => {
    return () => {
      if (!isUrlFile) {
        URL.revokeObjectURL(displayUrl);
      }
    };
  }, [displayUrl, isUrlFile]);

  /**
   * 화면 전체 덮개는 **document.body 에 그린다**(GS A3-04).
   *
   * 발주기관 채팅창은 가운데 정렬(transform)된 대화상자 안이라, 그 안에서 `fixed inset-0` 을
   * 그리면 화면이 아니라 채팅창 기준으로 갇혀 머리줄([다운로드]·[닫기])이 위로 잘렸다.
   * 포털로 빼되 React 트리는 그대로라 바깥 대화상자는 이 클릭을 «안쪽» 으로 본다.
   * 대화상자가 body 에 걸어 둔 `pointer-events: none` 을 피하려고 `pointer-events-auto` 를 준다.
   */
  const overlay = (
    <div
      ref={ref}
      data-slot="file-preview-modal"
      className={cn(
        "fixed inset-0 z-[220] pointer-events-auto",
        "flex items-center justify-center",
        "p-2 sm:p-4",
        "bg-slate-900/60  backdrop-blur-sm",
        "animate-in fade-in duration-200",
      )}
      role="dialog"
      aria-modal="true"
      aria-labelledby="preview-title"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div
        className={cn(
          "bg-white ",
          "w-full max-w-7xl h-[95vh]",
          "rounded-lg sm:rounded-xl shadow-2xl",
          "flex flex-col overflow-hidden",
          "animate-in zoom-in-95 slide-in-from-bottom-4 duration-300",
        )}
      >
        {/* Header Toolbar */}
        <div
          className={cn(
            "h-[56px] sm:h-[60px]",
            "border-b border-slate-200 ",
            "flex items-center justify-between",
            "px-3 sm:px-6",
            "bg-slate-50  shrink-0",
          )}
        >
          <div className="flex flex-col min-w-0">
            <h2
              id="preview-title"
              className="text-sm sm:text-base font-semibold text-slate-900 "
            >
              파일 미리보기
            </h2>
            <p
              className="text-2xs sm:text-xs text-slate-500  truncate max-w-[200px] sm:max-w-md"
              title={displayFileName}
            >
              {displayFileName}
            </p>
          </div>
          <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
            <Button
              variant="outline"
              size="sm"
              className={cn(
                "flex items-center justify-center",
                "h-8 sm:h-9 gap-1 sm:gap-2",
                "bg-white ",
                "text-xs sm:text-sm px-2 sm:px-3",
              )}
              onClick={handleDownload}
              aria-label="파일 다운로드"
            >
              <Download className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
              <span className="hidden sm:inline">다운로드</span>
            </Button>
            <div className="w-px h-4 bg-slate-300  mx-0.5 sm:mx-1" />
            <button
              onClick={onClose}
              className={cn(
                "p-1.5 sm:p-2",
                "hover:bg-slate-200 :bg-slate-700",
                "rounded-lg transition-colors",
                "focus:outline-none focus:ring-2 focus:ring-primary",
              )}
              aria-label="미리보기 닫기"
              title="ESC 키로도 닫을 수 있습니다"
            >
              <X className="w-4 h-4 sm:w-5 sm:h-5 text-slate-500 " />
            </button>
          </div>
        </div>

        {/* Preview Content */}
        <div
          className={cn(
            "flex-1 overflow-hidden",
            "bg-slate-100 ",
            "flex items-center justify-center p-4",
          )}
        >
          {canPreview ? (
            isImage ? (
              // Image Preview
              <div className="max-w-full max-h-full overflow-auto">
                <img
                  src={displayUrl}
                  alt={displayFileName}
                  className="max-w-full max-h-full object-contain rounded-lg shadow-lg"
                />
              </div>
            ) : isPdf ? (
              // PDF Preview
              <iframe
                src={displayUrl}
                title={displayFileName}
                className="w-full h-full border-0 rounded-lg shadow-lg"
                aria-label={`${displayFileName} PDF 파일 미리보기`}
              />
            ) : null
          ) : (
            // Cannot preview - show download prompt
            <div className="text-center space-y-4">
              <div className="flex justify-center mb-4">
                <AlertCircle className="w-12 h-12 text-slate-400" />
              </div>
              <div className="text-slate-600 ">
                <p className="text-lg font-medium mb-2">
                  미리보기를 지원하지 않는 파일 형식입니다
                </p>
                <p className="text-sm">
                  다운로드 버튼을 클릭하여 파일을 다운로드하세요
                </p>
              </div>
              <div className="text-sm text-slate-500 ">
                <p>파일명: {displayFileName}</p>
                <p>
                  파일 크기: {(displayFileSize / 1024 / 1024).toFixed(2)} MB
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return <BodyPortal>{overlay}</BodyPortal>;
});

FilePreviewModal.displayName = "FilePreviewModal";
